import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface MovemberDonationModalProps {
  open: boolean;
  onClose: () => void;
  onDonationComplete: () => void;
}

export function MovemberDonationModal({ open, onClose, onDonationComplete }: MovemberDonationModalProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const presetAmounts = [1, 2, 10, 100, 1000];

  const handleDonation = async () => {
    const amount = selectedAmount === -1 ? parseFloat(customAmount) : selectedAmount;

    if (amount === null || (selectedAmount === -1 && !customAmount)) {
      toast.error("Please select or enter a donation amount");
      return;
    }

    if (amount < 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Allow $0 donations (free generation)
    if (amount === 0) {
      toast.success("Thank you for spreading awareness! 🎗️");
      onDonationComplete();
      onClose();
      return;
    }

    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          amount: Math.round(amount * 100), // Convert to cents
          successUrl: `${window.location.origin}/movember?donation=success`,
          cancelUrl: `${window.location.origin}/movember?donation=cancelled`,
          metadata: {
            type: 'movember_donation',
            cause: 'Movember Foundation'
          }
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error("Failed to process donation. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            Support Movember Foundation
          </DialogTitle>
          <DialogDescription>
            Help us raise awareness for men's health. Choose a donation amount or generate for free.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-3 gap-2">
            {presetAmounts.map((amount) => (
              <Button
                key={amount}
                variant={selectedAmount === amount ? "default" : "outline"}
                onClick={() => {
                  setSelectedAmount(amount);
                  setCustomAmount("");
                }}
                className="h-12"
              >
                ${amount}
              </Button>
            ))}
            <Button
              variant={selectedAmount === -1 ? "default" : "outline"}
              onClick={() => setSelectedAmount(-1)}
              className="h-12"
            >
              Other
            </Button>
          </div>

          {selectedAmount === -1 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Custom Amount</label>
              <Input
                type="number"
                placeholder="Enter amount (min $0.00)"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleDonation}
              disabled={isProcessing}
              className="flex-1"
            >
              {isProcessing ? "Processing..." : selectedAmount === 0 || (selectedAmount === -1 && customAmount === "0") ? "Continue Free" : "Donate"}
            </Button>
            <Button
              onClick={() => {
                setSelectedAmount(0);
                handleDonation();
              }}
              variant="ghost"
              disabled={isProcessing}
            >
              Skip ($0)
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            All donations support the Movember Foundation's mission for men's health
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
